/* eslint-disable */
import dayjs, { Dayjs } from "dayjs";
import isToday from 'dayjs/plugin/isToday'
import isYesterday from 'dayjs/plugin/isYesterday'
import { EMPTY_CELL_VALUE } from "components";
import { MaybeNull, ParsedQueryParams } from "types";
import { ConfigParameterDTO } from "api";

dayjs.extend(isToday);
dayjs.extend(isYesterday);

export const isNullOrUndefined = (value: unknown) =>
  value === undefined || value === null;

export const checkNullValue = (value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return value;
}

export const definedValueOrDefault = <TValue, TDefault, TReturn = TDefault>(
  value: MaybeNull<TValue>,
  defaultValue: TDefault,
  mutationFn?: (value: TValue) => TReturn,
): TReturn | TDefault => {
  if (isNullOrUndefined(value)) {
    return defaultValue;
  }

  if (!mutationFn) {
    return value as unknown as TReturn;
  }

  return mutationFn(value!);
};

export const toUSNumber = (v: number | string, isCurrency = false) => {
  const navLanguage =
    window.sessionStorage.getItem("navigator_language") &&
    JSON.parse(window.sessionStorage.getItem("navigator_language") || "");

  return Number(v).toLocaleString(
    `${navLanguage || "en-GB"}`,
    isCurrency
      ? {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
      : undefined,
  );
};

export const getLoggedUserData = () => {
  const loggedUserData =
    window.sessionStorage.getItem("user") &&
    JSON.parse(window.sessionStorage.getItem("user") || "");

  return loggedUserData;
};

export const getUTCDateFormat = () => {
  const dateFormat =
    window.sessionStorage.getItem("date_format") &&
    JSON.parse(window.sessionStorage.getItem("date_format") || "");

  if (dateFormat === null) {
    return "MM-DD-YYYY";
  }
  return dateFormat;
};

export const getDateFormat = () => {
  const dateFormat =
    window.sessionStorage.getItem("date_format") &&
    JSON.parse(window.sessionStorage.getItem("date_format") || "");

  if (dateFormat === null) {
    return "MM-DD-YYYY";
  }
  return dateFormat === "DD-MM-YYYY" ? "D MMM, YYYY" : "MMM D, YYYY";
};

/**
 * Returns a date format string based on a saved user preference in sessionStorage,
 * optionally using a custom separator.
 *
 * If no preference is stored, it defaults to "DD-MM-YYYY" or "MM-DD-YYYY" depending
 * on the format, with the specified separator (if any).
 *
 * @param separator - Optional custom separator to use instead of dashes (e.g., "/", ".").
 * @returns A date format string in either "DD-MM-YYYY" or "MM-DD-YYYY" style with the chosen separator.
 */

export const getDateFormatset = (separator?: string) => {
  const dateFormat =
    window.sessionStorage.getItem("date_format") &&
    JSON.parse(window.sessionStorage.getItem("date_format") || "");

  const customSeparatorDateFormat = separator ? "DD-MM-YYYY".replaceAll("-", separator) : "DD-MM-YYYY";
  const customSeparatorDateFormatMonthBeforeDays = separator ? "MM-DD-YYYY".replaceAll("-", separator) : "MM-DD-YYYY";

  if (dateFormat === null) {
    return customSeparatorDateFormat;
  }
  return dateFormat === "DD-MM-YYYY" ? customSeparatorDateFormat : customSeparatorDateFormatMonthBeforeDays;
};

export const getDateFormatmonthset = () => {
  const dateFormat =
    window.sessionStorage.getItem("date_format") &&
    JSON.parse(window.sessionStorage.getItem("date_format") || "");

  if (dateFormat === null) {
    return "MM-YYYY";
  }
  return dateFormat === "DD-MM-YYYY" ? "MM-YYYY" : "MM-YYYY";
};

export const getFormattedDate = (date: string, separator?: string) => {
  if (!date) return null;
  const formattedDate = dayjs(date.substring(0, 10)).format(getDateFormatset());
  if (separator === '/') return formattedDate.replaceAll('-', '/');

  return formattedDate;
}

export const formatDateToISOStartOfDay = (date: Date) => `${date.toISOString().split('T')[0]}T00:00:00Z`;

export const buildQueryParams = (
  query: Record<string, string | number | boolean | string[] | number[]>,
) => {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length !== 0) {
        params.append(key, value.join());
      }
    } else if (!isNullOrUndefined(value) && value !== "" && value !== false) {
      params.append(key, String(value));
    }
  });

  return params;
};

export const getParsedQueryParams = (searchParams: URLSearchParams) => {
  const queryParams: ParsedQueryParams = {};

  searchParams.forEach((value, key) => {
    if (!queryParams[key]) {
      queryParams[key] = value;
    } else {
      if (!Array.isArray(queryParams[key])) {
        queryParams[key] = [queryParams[key]] as string[];
      }

      (queryParams[key] as string[]).push(value);
    }
  });

  return queryParams;
};

export const sortBy = <T>({
  array,
  key,
  order = "ASC",
}: {
  array: T[];
  key?: keyof T;
  order?: "ASC" | "DESC";
}) => {
  if (key) {
    if (order !== "ASC") {
      return array.sort((a, b) =>
        a[key] > b[key] ? -1 : b[key] > a[key] ? 1 : 0,
      );
    }

    return array.sort((a, b) =>
      a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0,
    );
  }

  if (order !== "ASC") {
    return array.sort((a, b) => (a > b ? -1 : b > a ? 1 : 0));
  }

  return array.sort((a, b) => (a > b ? 1 : b > a ? -1 : 0));
};

export const formatNumber = (num: number, decimalPlaces: number) => {
  const fixedNum = num?.toFixed(decimalPlaces);
  return parseFloat(fixedNum);
}

export const formatToUSNumber = (value: number) => {
  if (Number.isNaN(value) || isNullOrUndefined(value)) return EMPTY_CELL_VALUE;
  return new Intl.NumberFormat('en-US').format(value);
}

export function isValidISODate(dateString: string) {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
  return isoDateRegex.test(dateString);
}

export const convertBlankStringsToNull = (obj: any) => {
  const newObj: any = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (typeof obj[key] === "string" && obj[key].trim() === "") {
        newObj[key] = null;
      } else if (Array.isArray(obj[key])) {
        newObj[key] = obj[key].map((item: any) => convertBlankStringsToNull(item));
      } else if (typeof obj[key] === "object" && obj[key] !== null) {
        newObj[key] = convertBlankStringsToNull(obj[key]);
      } else {
        newObj[key] = obj[key];
      }
    }
  }

  return newObj;
};

export const getFirstAndLastDateOfFiscalYear = (fiscalYear: number, startMonth: string, end_Month: string,) => {
  const adjustedYear = startMonth.toUpperCase() === "JAN" ? fiscalYear : fiscalYear - 1;
  const startDate = dayjs(`${startMonth} 1, ${adjustedYear}`);
  const endDate = dayjs(`${end_Month} 1, ${fiscalYear}`).endOf("month");
  const firstDate = startDate.format("YYYY-MM-DD");
  const lastDate = endDate.format("YYYY-MM-DD");
  return { firstDate, lastDate };
};

export const isValidDate = (dateString: any) => {
  const regex = /^(19[9]\d|2\d{3})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
  return regex.test(dateString);
};

/**
 * Capitalizes the first letter and lowercases the rest of the string.
 *
 * @param str - The input string to format.
 * @returns A new string with only the first letter in uppercase and the rest in lowercase.
 */
export const capitalizeString = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Capitalizes the first letter of each word in a string, using a separator to split the input.
 * Each word is transformed to have an uppercase first letter and lowercase remaining letters.
 *
 * @param str - The input string to format.
 * @param sep - The separator used to split the input string into words (e.g., "-" or "_").
 * @returns A new string with each word capitalized and separated by a space.
 */
export const capitalizeAllStringWithSeparator = (str: string, sep: string): string => {
  if (!str) return "";
  return str.split(sep).map(word => capitalizeString(word)).join(" ");
}

/**
 * Formats a date string into a user-friendly representation like "Today", "Yesterday", 
 * or a short date format. Optionally includes the time in HH:mm:ss format.
 *
 * @param dateString - The ISO date string to format.
 * @param showTime - Whether to include the time portion in the output.
 * @returns A formatted string representing the date in a friendly way.
 *
 * Examples:
 * - If the date is today and `showTime` is false: "Today"
 * - If the date is yesterday and `showTime` is true: "Yesterday HH:mm:ss"
 * - For older dates: "Mon 05/20/2024" or "Mon 05/20/2024 HH:mm:ss"
 */
export function formatFriendlyDate(dateString: string, showTime: boolean): string {
  const date = dayjs(dateString).toString().substring(0, 25);

  let formattedString = '';

  if (dayjs(date).isToday()) {
    formattedString = 'Today';
  } else if (dayjs(date).add(-1, "day").isYesterday()) {
    formattedString = 'Yesterday';
  } else {
    formattedString = dayjs(date).format('ddd MM/DD/YYYY');
  }

  if (showTime && formattedString !== 'Today' && formattedString !== 'Yesterday') {
    formattedString += ` ${dayjs(date).format('HH:mm:ss')}`;
  } else if (showTime) {
    formattedString += ` ${dayjs(date).format('HH:mm:ss')}`;
  }

  return formattedString;
}

/**
 * Splits a string into an array using the provided separator and trims each resulting item.
 *
 * @param input - The input string to be split.
 * @param separator - The character or substring used to separate the input string.
 * @returns An array of trimmed strings. Returns an empty array if the input is empty or undefined.
 *
 */
export const parseStringBySeparator = (input: string, separator: string): string[] => {
  if (!input) return [];
  return input
    .split(separator)
    .map((item) => item.trim())
};

/**
 * Converts a label in the format "Mon 'YY" (e.g., "Jun '24" or "Dec '97") into a Date object 
 * representing the first day of that month. Automatically determines the correct century 
 * based on the year.
 *
 * @param label - A string label with the format "MMM 'YY", where MMM is the month abbreviation
 *                (e.g., "Jan", "Feb", "Mar") and 'YY is the two-digit year.
 * @returns A Date object set to the first day of the parsed month and year.
 *
 * Examples:
 * - "Jun '24" → new Date(2024, 5)
 * - "Dec '97" → new Date(1997, 11)
 */
export const parseLabelToDate = (label: string): Date => {
  const [monthStr, yearShort] = label.split(" ");
  const dateBeforeParsed = new Date(`01-${monthStr}-${yearShort.replace("'", "")}`);
  const month = dateBeforeParsed.getMonth();
  const year = dateBeforeParsed.getFullYear();
  return new Date(year, month);
};

/**
 * Sorts an array of date labels (in the format "MMM 'YY", e.g., "Jun '24" or "Dec '97") 
 * in ascending chronological order.
 *
 * Internally uses `parseLabelToDate` to convert each label into a Date object for comparison.
 *
 * @param dates - An array of strings representing dates in the "MMM 'YY" format.
 * @returns A new array with the same labels sorted from oldest to newest.
 *
 * Examples:
 * - ["Mar '23", "Jan '22", "Feb '23"] → ["Jan '22", "Feb '23", "Mar '23"]
 */
export const sortedDates = (dates: string[]) => [...dates].sort((a, b) => parseLabelToDate(a).getTime() - parseLabelToDate(b).getTime());

/**
 * Combines selected dropdown option arrays based on a corresponding list of boolean flags.
 *
 * @param {boolean[]} optionals - Array of boolean values indicating which option arrays to include.
 * Each `true` value at index `i` means `options[i]` will be included in the result.
 * @param {Array<any>[]} options - Array of arrays containing dropdown options.
 * Each inner array corresponds to a boolean in `optionals`.
 *
 * @returns {Array<any>} A flattened array containing only the option arrays marked as active (`true` in `optionals`).
 * Returns an empty array if the lengths of `optionals` and `options` do not match.
 */
export const combineDropdownOptions = (optionals: boolean[], options: Array<any>): Array<any> => {
  if (optionals.length !== options.length) return [];

  return optionals.reduce((acc, isActive, index) => {
    if (isActive) {
      acc.push(...options[index]);
    }
    return acc;
  }, [] as Array<any>);
};

/**
 * Formats a status label by appending the count of missing fields for warning statuses.
 * 
 * @param statusKey - The status key/label to format
 * @param missingFields - Array of missing field names
 * @returns The formatted status label. If statusKey is "Warning (Missing Fields)" 
 *          and there are missing fields, appends the count in parentheses (e.g., 
 *          "Warning (Missing Fields) (3)"). Otherwise returns the original statusKey.
 */
export const getStatusLabel = (statusKey: string, missingFields: string[]) => {
  if (statusKey === "Warning (Missing Fields)") {
    return `Warning (Missing Fields) ${missingFields.length ? `(${missingFields.length})` : ""}`;
  }
  return statusKey;
};

/**
 * Gets the corresponding color for a priority level indicator dot.
 * 
 * @param status - The status level (0-10)
 * @returns The hex color code string for the status level, or "none" if status is not mapped
 *
 */
export const getDotColor = (status: number) => {
  const colorMap: Record<number, string> = {
    0: "#DD1515",
    1: "#DD1515",
    2: "#DD1515",
    3: "#9EE766",
    4: "#9EE766",
    5: "#0E25D5",
    6: "#0E25D5",
    7: "#00A1FF",
    8: "#9EE766",
    9: "#7F7F7F",
    10: "#7F7F7F",
  };
  return colorMap[status] ?? "none";
};

/**
 * Determines the algorithm name based on service, source, and routing configuration.
 * 
 * @param row - Object containing service, source, and routing properties
 * @param row.service - The service type (e.g., "default")
 * @param row.source - The data source (e.g., "idpv1", "abbyy")
 * @param row.routing - The routing configuration (e.g., "fallback", "default")
 * @returns The algorithm name string based on the configuration, or "-" if no match found
 */
export const determineAlgorithmName = (row: any): string => {
  const { service, source, routing } = row;

  if (service === "default" && source === "idpv1" && routing === "fallback") {
    return "IDP Fallback";
  }

  if (service === "default" && source === "idpv1" && routing === "default") {
    return "IDP A/B Testing";
  }

  if (service === "default" && source === "abbyy") {
    return "Current";
  }

  return "-";
};

/**
 * Determines whether a given feature is enabled based on configuration parameters.
 *
 * Searches for a configuration parameter matching the provided feature name and evaluates
 * its `configValue`. Supports boolean values (`true`/`false`) and string representations
 * such as `"true"` or `"on"`.
 *
 * @param {ConfigParameterDTO[] | undefined} configParameter - The list of configuration parameters to search in.
 * @param {string} featureName - The name of the feature to check.
 * @returns {boolean} `true` if the feature is enabled, otherwise `false`.
 */
export const isFeatureEnabled = (configParameter: ConfigParameterDTO[] | undefined, featureName: string): boolean => {
  const featureValue = configParameter?.find((setting: ConfigParameterDTO) => setting.name === featureName)?.configValue;
  return typeof featureValue === "boolean" ? featureValue : featureValue === "true" || featureValue?.toLocaleLowerCase() === "on";
};

/**
 * Retrieves a configuration parameter object from a list by matching its `name` property with the provided feature name.
 *
 * @param {ConfigParameterDTO[] | undefined} configParameter - The list of configuration parameters to search in.
 * @param {string} featureName - The name of the feature whose configuration parameter should be retrieved.
 * @returns {ConfigParameterDTO | undefined} The matching configuration parameter if found, otherwise `undefined`.
 */
export const retrieveConfigParameter = (configParameter: ConfigParameterDTO[] | undefined, featureName: string): ConfigParameterDTO | undefined => {
  return configParameter?.find((setting: ConfigParameterDTO) => setting.name === featureName);
};

/**
 * Determines whether the difference between two dates exceeds one month.
 *
 * @param {Dayjs} date1 - The starting date.
 * @param {Dayjs} date2 - The ending date.
 * @returns {boolean} `true` if the difference between `date1` and `date2` is greater than one month, otherwise `false`.
 */
export const isMoreThanOneMonthDifference = (date1: Dayjs, date2: Dayjs): boolean => {
  const difference = date2.diff(date1, 'month', true);
  return difference > 1;
};